import React from 'react';
import { Box, Skeleton, LinearProgress } from '@mui/material';

const TopProgress = () => (
  <LinearProgress
    sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      height: 3,
      '& .MuiLinearProgress-bar': {
        background: 'linear-gradient(90deg, #6366f1, #8b5cf6)'
      },
      bgcolor: 'transparent'
    }}
  />
);

const ContentSkeleton = () => (
  <>
    {/* Page title */}
    <Skeleton variant="text" width={220} height={38} sx={{ mb: 0.5 }} />
    <Skeleton variant="text" width={340} height={22} sx={{ mb: 3 }} />

    {/* Stat cards row */}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 2 }} />
      ))}
    </Box>

    {/* Content cards */}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
    </Box>
  </>
);

/**
 * ContentLoader — Suspense fallback used inside Layout around <Outlet />.
 * Only the content area is replaced, so Navbar/Sidebar stay mounted while a
 * page chunk downloads (no full-screen "reload" flash between menu clicks).
 */
export const ContentLoader = () => (
  <Box sx={{ width: '100%' }}>
    <TopProgress />
    <ContentSkeleton />
  </Box>
);

/**
 * PageLoader — shown via React.lazy Suspense while a page chunk is downloading.
 * Mimics a generic page shell: top bar progress + card skeletons.
 */
const PageLoader = () => (
  <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
    <TopProgress />

    {/* Fake header bar */}
    <Box sx={{
      height: 64, px: 3,
      display: 'flex', alignItems: 'center', gap: 2,
      borderBottom: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper'
    }}>
      <Skeleton variant="rounded" width={120} height={32} />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
    </Box>

    <Box sx={{ display: 'flex' }}>
      {/* Fake sidebar */}
      <Box sx={{
        width: 220, flexShrink: 0, p: 2,
        borderRight: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', minHeight: 'calc(100vh - 64px)',
        display: { xs: 'none', md: 'block' }
      }}>
        <Skeleton variant="rounded" width="80%" height={20} sx={{ mb: 2 }} />
        {Array.from({ length: 7 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={`${50 + Math.random() * 40}%`} />
          </Box>
        ))}
        <Skeleton variant="rounded" width="80%" height={20} sx={{ mt: 3, mb: 2 }} />
        {Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={`${50 + Math.random() * 30}%`} />
          </Box>
        ))}
      </Box>

      {/* Main content area */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
        <ContentSkeleton />
      </Box>
    </Box>
  </Box>
);

export default PageLoader;
